import { Component, ReactNode } from 'react';
import styled from 'styled-components';
import { IconButton } from '../IconButton/IconButton';
import { Heading } from '../Heading/Heading';

const DEFAULT_PADDING = 20;

const PanelElement = styled.div<IProps>`
  display: ${props => (props.sidebar != null ? 'grid' : 'block')};
  grid-template-areas: 'content sidebar';
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  background-color: ${props => props.backgroundColor || 'white'};
  border: ${({ theme, variation }) => (variation === 'clean' ? 'none' : `1px solid ${theme.vars.newColor.lightGS1}`)};
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  width: ${props => (props.width ? props.width + 'px' : 'auto')};
  flex: 1;
  margin-bottom: 3px;
  height: 100%;
`;

const HeadingWrapper = styled.div`
  margin-bottom: 30px;
`;

const PanelContentElement = styled.div`
  grid-area: content;
  height: 100%;
  position: relative;
`;

const PanelContentInnerElement = styled.div<IProps>`
  height: 100%;
  padding: ${props => (props.padding != null ? props.padding : DEFAULT_PADDING)}px;
`;

const PanelSidebarElement = styled.div<IProps>`
  grid-area: sidebar;
  border-left: ${props => (props.showSidebar ? '1px solid #f5f9fc;' : 'none')};
  max-width: ${props => (props.showSidebar ? '400px' : '0px')};
  transition: max-width 0.5s;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PanelSidebarContentElement = styled.div<IProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${props => (props.padding != null ? props.padding : DEFAULT_PADDING)}px;
`;

const PanelFooterElement = styled.div<IProps>`
  padding-left: ${props => (props.padding != null ? props.padding : 20)}px;
  padding-right: ${props => (props.padding != null ? props.padding : 20)}px;
`;

const CloseButtonElement = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`;

export type PanelVariation = 'normal' | 'clean';

export interface IProps {
  /** Whether to show a close button at top-right of panel.  Defaults to false */
  closable?: boolean;
  /** Optional heading text.  Defaults to undefined. */
  heading?: string;
  /** Background color of panel.  Defaults to "white". */
  backgroundColor?: string;
  /** Custom width of the panel.  Defaults to "auto". */
  width?: number;
  /** Padding in pixels. Defaults to 20px. */
  padding?: number;
  /** The panel variation.  Can be either "normal" (default) and "clean". */
  variation?: PanelVariation;
  /** Optional sidebar slot for sidebar content. Defaults to undefined. */
  sidebar?: ReactNode;
  /** Sidebar content is displayed when true. Defaults to false. */
  showSidebar?: boolean;
  /** Event fired when panel is clicked. Defaults to undefined. */
  onClick?: (e: any) => void;
  /** Event fired close button is clicked. Defaults to undefined. */
  onCloseButtonClicked?: (e: any) => void;
  /** Optional footer slot for custom footer content.  Defaults to undefined. */
  footer?: ReactNode;
}

export class Panel extends Component<IProps> {
  public static defaultProps: Pick<IProps, 'showSidebar' | 'closable'> = {
    showSidebar: true,
    closable: false,
  };

  public render() {
    return (
      <PanelElement
        data-testid="Panel"
        variation={this.props.variation}
        onClick={this.props.onClick}
        width={this.props.width}
        backgroundColor={this.props.backgroundColor}
        sidebar={this.props.sidebar}
      >
        <PanelContentElement>
          {this.props.closable && (
            <CloseButtonElement>
              <IconButton icon="times" onClick={this.props.onCloseButtonClicked} ariaLabel="Close" />
            </CloseButtonElement>
          )}
          <PanelContentInnerElement padding={this.props.padding}>
            {(this.props.heading || this.props.closable) && (
              <HeadingWrapper>
                <Heading variation="h2">{this.props.heading}</Heading>
              </HeadingWrapper>
            )}
            {this.props.children}
          </PanelContentInnerElement>
          {this.props.footer && (
            <PanelFooterElement padding={this.props.padding}>{this.props.footer}</PanelFooterElement>
          )}
        </PanelContentElement>

        {this.props.sidebar && (
          <PanelSidebarElement showSidebar={this.props.showSidebar}>
            <PanelSidebarContentElement padding={this.props.padding}>{this.props.sidebar}</PanelSidebarContentElement>
          </PanelSidebarElement>
        )}
      </PanelElement>
    );
  }
}
